import React, { useState, useEffect } from 'react';
import {
  Timeline,
  Space,
  Avatar,
  Typography,
  Empty,
  Spin,
  Tag,
  Button,
  Card,
} from 'antd';
import {
  PhoneOutlined,
  MessageOutlined,
  MailOutlined,
  TeamOutlined,
  RobotOutlined,
  PlusOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { getFollowUps, FollowUp } from '../../../../services/followUp';
import AddFollowUpModal from './AddFollowUpModal';
import FilePreview from '../shared/FilePreview';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('zh-cn');
dayjs.tz.setDefault('Asia/Shanghai');

const { Text } = Typography;

interface CaseFollowUpTabProps {
  caseId: number;
  canEdit: boolean;
}

interface AttachmentPreview {
  id: number;
  original_filename: string;
  file_url: string;
  content_type?: string;
}

const CaseFollowUpTab: React.FC<CaseFollowUpTabProps> = ({ caseId, canEdit }) => {
  const [loading, setLoading] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<AttachmentPreview | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);

  // Load follow-ups
  const loadFollowUps = async (pageNum: number = 1, append: boolean = false) => {
    if (!caseId) return;

    setLoading(true);
    try {
      const response = await getFollowUps({
        entity_type: 'case',
        entity_id: caseId,
        page: pageNum,
        pageSize: 20,
      });

      if (append) {
        setFollowUps(prev => [...prev, ...(response.items || [])]);
      } else {
        setFollowUps(response.items || []);
      }

      setTotal(response.total || 0);
      setHasMore(
        (response.items || []).length === 20 &&
        followUps.length + (response.items || []).length < response.total
      );
    } catch (error) {
      console.error('Failed to load follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load more follow-ups
  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadFollowUps(nextPage, true);
  };

  // Initial load
  useEffect(() => {
    if (caseId) {
      setPage(1);
      loadFollowUps(1, false);
    }
  }, [caseId]);

  // Handle modal success
  const handleModalSuccess = () => {
    setModalVisible(false);
    setPage(1);
    loadFollowUps(1, false);
  };

  // Handle attachment preview - 复用附件管理的逻辑
  const handleAttachmentPreview = async (attachment: any) => {
    try {
      // 如果附件有 id，通过 API 获取下载链接（与附件管理一致）
      if (attachment.id) {
        const { get } = await import('../../../../utils/request');
        const response = await get<{ download_url: string; filename?: string }>(
          `/attachments/${attachment.id}/download`
        );
        
        const previewData = {
          id: attachment.id,
          // 优先使用 API 返回的 filename（包含扩展名）
          original_filename: response.filename || attachment.original_filename || attachment.file_name || attachment.name || '附件',
          file_url: response.download_url,
          content_type: attachment.content_type || attachment.file_type,
        };
        
        setPreviewFile(previewData);
        setPreviewVisible(true);
      } else {
        // 如果没有 id，直接使用 file_path 构建 URL
        const filePath = attachment.file_path || attachment.url;
        if (!filePath) return;
        
        const getAttachmentUrl = () => {
          if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
            return filePath;
          }
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
          const apiBase = baseUrl.replace('/api/v1', '');
          return `${apiBase}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
        };
        
        const previewData = {
          id: 0,
          original_filename: attachment.original_filename || attachment.file_name || attachment.name || '附件',
          file_url: getAttachmentUrl(),
          content_type: attachment.content_type || attachment.file_type,
        };
        
        setPreviewFile(previewData);
        setPreviewVisible(true);
      }
    } catch (error: any) {
      console.error('获取文件预览失败:', error);
    }
  };

  // Handle attachment download - 复用附件管理的逻辑
  const handleAttachmentDownload = async (attachment: any) => {
    try {
      let downloadUrl: string;
      let filename: string;
      
      if (attachment.id) {
        const { get } = await import('../../../../utils/request');
        const response = await get<{ download_url: string; filename: string }>(
          `/attachments/${attachment.id}/download`
        );
        downloadUrl = response.download_url;
        filename = response.filename || attachment.original_filename || attachment.file_name || '附件';
      } else {
        const filePath = attachment.file_path || attachment.url;
        if (!filePath) return;
        
        if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
          downloadUrl = filePath;
        } else {
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
          const apiBase = baseUrl.replace('/api/v1', '');
          downloadUrl = `${apiBase}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
        }
        filename = attachment.original_filename || attachment.file_name || attachment.name || '附件';
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error('下载失败:', error);
    }
  };

  // Get follow-up type icon
  const getFollowUpIcon = (type?: string) => {
    if (!type) return <MessageOutlined />;
    const iconMap: Record<string, React.ReactNode> = {
      phone: <PhoneOutlined style={{ color: '#1890ff' }} />,
      电话: <PhoneOutlined style={{ color: '#1890ff' }} />,
      message: <MessageOutlined style={{ color: '#52c41a' }} />,
      短信: <MessageOutlined style={{ color: '#52c41a' }} />,
      email: <MailOutlined style={{ color: '#faad14' }} />,
      邮件: <MailOutlined style={{ color: '#faad14' }} />,
      meeting: <TeamOutlined style={{ color: '#722ed1' }} />,
      会议: <TeamOutlined style={{ color: '#722ed1' }} />,
      面谈: <TeamOutlined style={{ color: '#722ed1' }} />,
      system: <RobotOutlined style={{ color: '#8c8c8c' }} />,
      系统: <RobotOutlined style={{ color: '#8c8c8c' }} />,
    };
    return iconMap[type] || <MessageOutlined />;
  };

  // Get follow-up type text
  const getFollowUpTypeText = (type?: string) => {
    if (!type) return '其他';
    const textMap: Record<string, string> = {
      phone: '电话',
      message: '短信',
      email: '邮件',
      meeting: '会议',
      visit: '拜访',
      面谈: '面谈',
      system: '系统',
      other: '其他',
    };
    return textMap[type] || type;
  };

  if (loading && followUps.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div className="case-follow-up-tab">
      <Card>
        {/* Header with Add Button */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text strong style={{ fontSize: 16 }}>跟进记录</Text>
            {total > 0 && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({total})
              </Text>
            )}
          </div>
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              添加跟进
            </Button>
          )}
        </div>

        {/* Timeline */}
        {followUps.length === 0 ? (
          <Empty
            description="暂无跟进记录"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <>
            <Timeline>
              {followUps.map((followUp) => {
                const followType = followUp.follow_up_type || followUp.follow_type;
                const isSystem = followType === 'system' || followType === '系统';

                return (
                  <Timeline.Item
                    key={followUp.id}
                    dot={
                      <Avatar
                        size="small"
                        icon={isSystem ? <RobotOutlined /> : <MessageOutlined />}
                        style={{
                          backgroundColor: isSystem ? '#f5f5f5' : '#e6f7ff',
                        }}
                      />
                    }
                    color={isSystem ? 'gray' : 'blue'}
                  >
                    <div className={`timeline-item-content ${isSystem ? 'timeline-item-system' : ''}`}>
                      <Space direction="vertical" size={4} style={{ width: '100%', maxWidth: '100%' }}>
                        <Space size="small" wrap style={{ maxWidth: '100%' }}>
                          <Text strong>
                            {followUp.created_by_name || followUp.creator_name || '未知用户'}
                          </Text>
                          {isSystem && (
                            <Tag color="default" style={{ margin: 0 }}>
                              系统操作
                            </Tag>
                          )}
                        </Space>
                        <div style={{ width: '100%', maxWidth: '100%' }}>
                          <Text
                            style={{
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              display: 'block',
                            }}
                          >
                            {followUp.content}
                          </Text>
                        </div>
                        
                        {/* Attachments */}
                        {followUp.attachments && followUp.attachments.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <Space size="small" wrap>
                              {followUp.attachments.map((attachment: any, index: number) => {
                                const filePath = attachment.file_path || attachment.url;
                                const fileName = attachment.file_name || attachment.name || `附件${index + 1}`;
                                
                                // 获取文件图标
                                const getFileIcon = () => {
                                  const fileType = attachment.file_type || fileName.toLowerCase();
                                  
                                  if (fileType.includes('pdf')) {
                                    return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
                                  }
                                  if (fileType.includes('image') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName)) {
                                    return <FileImageOutlined style={{ color: '#52c41a' }} />;
                                  }
                                  if (fileType.includes('word') || /\.(doc|docx)$/i.test(fileName)) {
                                    return <FileWordOutlined style={{ color: '#1890ff' }} />;
                                  }
                                  if (fileType.includes('excel') || /\.(xls|xlsx)$/i.test(fileName)) {
                                    return <FileExcelOutlined style={{ color: '#52c41a' }} />;
                                  }
                                  return <FileOutlined />;
                                };
                                
                                // 构建完整的URL
                                const getAttachmentUrl = () => {
                                  if (!filePath) return null;
                                  
                                  // 如果已经是完整URL，直接返回
                                  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
                                    return filePath;
                                  }
                                  
                                  // 如果是相对路径，拼接API基础URL
                                  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
                                  const apiBase = baseUrl.replace('/api/v1', '');
                                  return `${apiBase}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
                                };
                                
                                const attachmentUrl = getAttachmentUrl();
                                
                                // 格式化文件大小
                                const formatFileSize = (bytes?: number) => {
                                  if (!bytes || bytes === 0) return '';
                                  const k = 1024;
                                  const sizes = ['B', 'KB', 'MB', 'GB'];
                                  const i = Math.floor(Math.log(bytes) / Math.log(k));
                                  return ` (${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]})`;
                                };
                                
                                return (
                                  <Tag
                                    key={index}
                                    icon={getFileIcon()}
                                    color="default"
                                    style={{ 
                                      cursor: attachmentUrl ? 'pointer' : 'not-allowed',
                                      padding: '4px 8px',
                                      fontSize: 13
                                    }}
                                    onClick={() => {
                                      if (attachmentUrl) {
                                        handleAttachmentPreview(attachment);
                                      }
                                    }}
                                  >
                                    <Space size={4}>
                                      <span>{fileName}</span>
                                      {attachment.file_size && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {formatFileSize(attachment.file_size)}
                                        </Text>
                                      )}
                                      <DownloadOutlined 
                                        style={{ fontSize: 12 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAttachmentDownload(attachment);
                                        }}
                                      />
                                    </Space>
                                  </Tag>
                                );
                              })}
                            </Space>
                          </div>
                        )}

                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs.utc(followUp.created_at).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm')}
                          {' · '}
                          {dayjs.utc(followUp.created_at).tz('Asia/Shanghai').fromNow()}
                        </Text>
                      </Space>
                    </div>
                  </Timeline.Item>
                );
              })}
            </Timeline>

            {/* Load More Button */}
            {hasMore && followUps.length > 0 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button
                  type="link"
                  onClick={loadMore}
                  loading={loading}
                  size="small"
                >
                  加载更多 ({followUps.length} / {total})
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Add Follow-up Modal */}
      <AddFollowUpModal
        visible={modalVisible}
        caseId={caseId}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
      />

      {/* File Preview Modal */}
      {previewFile && (
        <FilePreview
          visible={previewVisible}
          fileUrl={previewFile.file_url}
          fileName={previewFile.original_filename}
          fileType={previewFile.content_type}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewFile(null);
          }}
          onDownload={() => {
            if (previewFile.id) {
              handleAttachmentDownload({ id: previewFile.id, original_filename: previewFile.original_filename });
            } else {
              const link = document.createElement('a');
              link.href = previewFile.file_url;
              link.download = previewFile.original_filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }}
        />
      )}
    </div>
  );
};

export default CaseFollowUpTab;
